import { cn } from './cn';

export interface CountBadgeProps {
  count?: number;
  /** Above this, render `${max}+`. Mirrors antd Badge overflowCount (default 99). */
  max?: number;
  className?: string;
}

/**
 * Small red count pill (replaces antd Badge for "pending work" indicators).
 *
 * Per the project rule, pending counts must surface on every actionable
 * surface — nav items, tabs, queues — so this renders nothing only when the
 * count is zero/absent, and is safe to drop next to any label.
 */
export function CountBadge({ count, max = 99, className }: CountBadgeProps) {
  if (!count || count <= 0) return null;
  return (
    <span
      className={cn(
        'inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-pill px-[5px]',
        'bg-danger-500 text-[11px] font-bold leading-none text-white tabular-nums',
        'ring-2 ring-surface', // separates the pill from whatever it overlaps
        className,
      )}
    >
      {count > max ? `${max}+` : count}
    </span>
  );
}

/** Bare status dot (no number) — for "has activity" hints. */
export function Dot({ className }: { className?: string }) {
  return <span className={cn('inline-block h-2 w-2 rounded-pill bg-danger-500', className)} />;
}

const STATUS_DOT: Record<string, string> = {
  success: 'bg-brand-500', error: 'bg-danger-500', warning: 'bg-warning-500',
  processing: 'bg-sky-500', default: 'bg-subtle',
};

/**
 * Ant Design `Badge` replacement — a superscript count/dot on a wrapped child,
 * a standalone count/dot, or the `status`+`text` variant.
 */
export function Badge({
  count, dot, showZero, overflowCount = 99, status, text, color, size, children, className,
}: {
  count?: number;
  dot?: boolean;
  showZero?: boolean;
  overflowCount?: number;
  status?: 'success' | 'error' | 'warning' | 'processing' | 'default';
  text?: import('react').ReactNode;
  color?: string;
  size?: 'default' | 'small';
  children?: import('react').ReactNode;
  className?: string;
}) {
  // status + text variant (a coloured dot beside a label)
  if (status || (text != null && count == null && !dot)) {
    return (
      <span className={cn('inline-flex items-center gap-1.5 text-sm text-content', className)}>
        <span className={cn('size-2 shrink-0 rounded-full', STATUS_DOT[status ?? 'default'])} style={color ? { background: color } : undefined} />
        {text}
      </span>
    );
  }
  const num = typeof count === 'number' ? (count > overflowCount ? `${overflowCount}+` : count) : count;
  const visible = dot || (count != null && (count !== 0 || showZero));
  const bg = color ? undefined : 'bg-danger-500';

  const pill = dot ? (
    <span className={cn('size-2 rounded-full ring-2 ring-surface', bg)} style={color ? { background: color } : undefined} />
  ) : (
    <span className={cn('grid h-[18px] min-w-[18px] place-items-center rounded-pill px-[5px] text-[11px] font-bold leading-none text-white ring-2 ring-surface tabular-nums', bg, size === 'small' && 'h-3.5 min-w-3.5 text-[10px]')} style={color ? { background: color } : undefined}>
      {num}
    </span>
  );

  if (children == null) return visible ? pill : null;
  return (
    <span className={cn('relative inline-flex', className)}>
      {children}
      {visible && <span className="absolute -right-1 -top-1.5">{pill}</span>}
    </span>
  );
}
